import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ImagePicker from "@/components/admin/ImagePicker";
import { toast } from "sonner";

export default function SettingsManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/settings").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/settings", data);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const updSocial = (k, v) => setData({ ...data, social: { ...(data.social || {}), [k]: v } });

  if (!data) return <div className="text-earth-600">Loading…</div>;

  return (
    <div data-testid="settings-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Global</div>
        <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Settings</h1>
      </div>

      <div className="bg-white rounded-2xl p-8 border border-earth-100 flex flex-col gap-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>WhatsApp number (with country code)</Label>
            <Input className="mt-2" value={data.whatsapp_number || ""} onChange={(e) => setData({ ...data, whatsapp_number: e.target.value })} data-testid="settings-wa" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input className="mt-2" value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} data-testid="settings-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input className="mt-2" value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} data-testid="settings-email" />
          </div>
          <div>
            <Label>Logo</Label>
            <div className="mt-2">
              <ImagePicker label="" value={data.logo_url} onChange={(v) => setData({ ...data, logo_url: v })} testId="settings-logo" aspect="aspect-[3/1]" />
            </div>
          </div>
        </div>

        <div className="border-t border-earth-100 pt-5">
          <div className="font-serif text-xl text-earth-900 mb-3">Social links</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Input placeholder="Instagram URL" value={data.social?.instagram || ""} onChange={(e) => updSocial("instagram", e.target.value)} data-testid="settings-ig" />
            <Input placeholder="Facebook URL" value={data.social?.facebook || ""} onChange={(e) => updSocial("facebook", e.target.value)} data-testid="settings-fb" />
            <Input placeholder="YouTube URL" value={data.social?.youtube || ""} onChange={(e) => updSocial("youtube", e.target.value)} data-testid="settings-yt" />
          </div>
        </div>

        <div className="border-t border-earth-100 pt-5">
          <div className="font-serif text-xl text-earth-900 mb-3">SEO</div>
          <div className="flex flex-col gap-3">
            <Input placeholder="SEO title" value={data.seo_title || ""} onChange={(e) => setData({ ...data, seo_title: e.target.value })} data-testid="settings-seo-title" />
            <Textarea rows={3} placeholder="SEO description" value={data.seo_description || ""} onChange={(e) => setData({ ...data, seo_description: e.target.value })} data-testid="settings-seo-desc" />
          </div>
        </div>

        <div>
          <Button onClick={save} disabled={saving} className="bg-earth-900 text-white hover:bg-earth-800 rounded-full px-8" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
